import React from 'react';
import { useParams } from 'react-router-dom';
import _get from 'lodash/get';
import { Site, useSiteQuery } from 'graphql-common';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import TabCountersContext from '@lib/contexts/TabCountersContext';
import useTabCounters from '@lib/hooks/useTabCounters';
import SiteMenuBar from 'components/SiteMenuBar/SiteMenuBar';
import SiteContainer from './SiteContainer';
import SiteAreasContainer from './SiteAreasContainer';
import SiteUsersContainer from './SiteUsersContainer';
import { SiteTabViewMode, SiteViewMode } from './enums';

interface Props {
  viewMode: SiteTabViewMode;
}

/**
 * Shared shell for the site tabs. Each tab is its own route, but they all
 * resolve to this component so React reconciles instead of remounting on a tab
 * click — only the container below the menu is swapped. Rendering `SiteMenuBar`
 * inside each tab's view instead would mount a fresh `TabBar` every time, and
 * its sliding active indicator can't animate across a remount.
 */
export default function SiteTabsPage({ viewMode }: Props) {
  const { id = '' } = useParams();

  // Same query the tab containers run, so this hits the Apollo cache rather
  // than adding a request — it is only here for the tab counters.
  const siteQuery = useSiteQuery({
    skip: !id,
    variables: { findBy: { id } },
  });
  const siteData = _get(siteQuery, 'data.site') as Site;

  // Counters live on the site entity, but the tabs mutate its children (areas,
  // memberships). Refresh them on every tab switch, and let the tab containers
  // ask for a refresh via `useTabCountersContext()` after their own mutations.
  const tabCounters = useTabCounters({
    refetch: siteQuery.refetch,
    skip: !id,
  });

  let content: React.ReactNode = null;

  if (viewMode === SiteTabViewMode.About) {
    content = <SiteContainer viewMode={SiteViewMode.View} />;
  } else if (viewMode === SiteTabViewMode.Areas) {
    content = <SiteAreasContainer />;
  } else if (viewMode === SiteTabViewMode.Users) {
    content = <SiteUsersContainer />;
  }

  return (
    <PageBody withoutHeader>
      <SiteMenuBar
        id={id}
        siteAreasCount={siteData?.siteAreasCount}
        usersCount={siteData?.usersCount}
      />
      <TabCountersContext.Provider value={tabCounters}>
        {content}
      </TabCountersContext.Provider>
    </PageBody>
  );
}
